import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Primary — the ShopTrack blue. Used for one action per view.
        default:
          'bg-accent text-onAccent hover:bg-accentSoft',
        // Secondary — muted paper button
        secondary:
          'bg-paperSoft text-ink hover:bg-rule',
        // Outline — quiet action
        outline:
          'border border-rule bg-transparent text-ink hover:bg-paperSoft',
        // Ghost — nav / row action
        ghost:
          'text-ink hover:bg-paperSoft',
        // Danger — destructive confirm
        destructive:
          'bg-danger text-white hover:bg-danger/90',
        // Link — inline text
        link: 'text-accent underline-offset-4 hover:underline p-0 h-auto',
      },
      // Cỡ chạm theo CHẾ ĐỘ, không theo bề rộng. Bản cũ dùng `sm:` (640px):
      // ngón tay cần 44px, chuột thì 36–40px là đủ và gọn hơn — nhưng iPad
      // rộng 768–1366px vẫn là ngón tay. Với `sm:` thì mọi nút trên iPad co
      // xuống 36px, tức là cả app khó bấm đúng ở thiết bị được nhắm tới.
      size: {
        default: 'h-11 desktop:h-10 px-4 py-2',
        sm: 'h-11 desktop:h-9 px-3 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'size-11 desktop:size-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        data-slot="button"
        data-variant={variant ?? 'default'}
        data-size={size ?? 'default'}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
